import useDispatch from "hooks/dispatch/useDispatch";
import DispatchRequest from "./components/DispatchRequest";
import { APIProvider, Map, Marker, useApiIsLoaded } from "@vis.gl/react-google-maps";
import { GOOGLE_API_KEY } from "constants/common";
import pickupIcon from "assets/images/pickup.png";
import dropOff from "assets/images/drop-off.png";

const DispatchMapMarkers = ({
  driverLocation,
  pickupLocation,
  dropOffLocation,
  requestLoading,
  handleSetDriver,
}: any) => {
  const apiIsLoaded = useApiIsLoaded();
  if (!apiIsLoaded || typeof google === "undefined") return null;

  return (
    <>
      {driverLocation.map((driver: any) => (
        <Marker
          key={driver.id}
          onClick={requestLoading.loading ? undefined : () => handleSetDriver(driver)}
          icon={{
            url: driver.fleet.fleet_model.image,
            size: new google.maps.Size(50, 40),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(0, 40),
            scaledSize: new google.maps.Size(50, 40),
            rotation: 1,
          }}
          opacity={requestLoading.loading ? 0.5 : 1}
          optimized
          position={{ lat: driver.latitude, lng: driver.longitude }}
        />
      ))}
      {pickupLocation && (
        <Marker
          key={"pickup"}
          icon={{
            url: pickupIcon,
            size: new google.maps.Size(40, 30),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(20, 30),
            scaledSize: new google.maps.Size(40, 30),
            rotation: 1,
          }}
          optimized
          position={pickupLocation}
        />
      )}
      {dropOffLocation && (
        <Marker
          key={"destination"}
          icon={{
            url: dropOff,
            size: new google.maps.Size(36, 30),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(18, 40),
            scaledSize: new google.maps.Size(36, 30),
            rotation: 1,
          }}
          optimized
          position={dropOffLocation}
        />
      )}
    </>
  );
};

export default function DispatchPage() {
  const {
    control,
    pickupSuggestion,
    pickupSuggestionLoading,
    dropOffSuggestion,
    dropOffSuggestionLoading,
    requestLoading,
    currentLocation,
    watchDriverId,
    pickupLocation,
    dropOffLocation,
    models: { data, loading },
    activeMarkerType,
    tripEstimation,
    handleSetDriver,
    handleClearDriver,
    handleTogglePickupLocation,
    handleToggleDestinationLocation,
    onDropOffSelect,
    onPickupSelect,
    onPickupSearch,
    onDropOffSearch,
    handleSubmit,
    onSubmit,
    handleCancelTrip,
    handleMapClick,
    handleClearForm,
    // FIX P2: New enhanced dispatch UI features
    selectedDriverInfo,
    dispatchProgress,
    driverSearchQuery,
    filteredDrivers,
    handleDriverSearch,
  } = useDispatch();

  return (
    <div className="w-full p-2 flex flex-col gap-4">
      <div className="flex justify-between gap-4">
        <div className="flex flex-col gap-3" style={{ width: "420px", minWidth: "380px" }}>
          <DispatchRequest
            handleClearForm={handleClearForm}
            handleCancelTrip={handleCancelTrip}
            control={control}
            tripEstimation={tripEstimation}
            activeMarkerType={activeMarkerType}
            data={data}
            watchDriverId={watchDriverId}
            loading={loading}
            pickupSuggestion={pickupSuggestion ?? []}
            pickupSuggestionLoading={pickupSuggestionLoading}
            dropOffSuggestion={dropOffSuggestion ?? []}
            dropOffSuggestionLoading={dropOffSuggestionLoading}
            requestLoading={requestLoading}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            handleClearDriver={handleClearDriver}
            onPickupSelect={onPickupSelect}
            onDropOffSelect={onDropOffSelect}
            onPickupSearch={onPickupSearch}
            onDropOffSearch={onDropOffSearch}
            handleToggleDestinationLocation={handleToggleDestinationLocation}
            handleTogglePickupLocation={handleTogglePickupLocation}
          />

          {/* FIX P2: Selected Driver Info Card */}
          {selectedDriverInfo && (
            <div className="bg-white shadow-lg rounded-lg p-4" id="selected-driver-info">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Selected Driver</h3>
                <button
                  onClick={handleClearDriver}
                  className="text-xs text-red-500 hover:text-red-700 cursor-pointer"
                >
                  ✕ Clear
                </button>
              </div>
              <div className="flex items-center gap-3">
                <img
                  src={selectedDriverInfo.image}
                  alt={selectedDriverInfo.first_name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-blue-500"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://ui-avatars.com/api/?name=" +
                      selectedDriverInfo.first_name +
                      "+" +
                      selectedDriverInfo.last_name;
                  }}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">
                    {selectedDriverInfo.first_name} {selectedDriverInfo.middle_name}{" "}
                    {selectedDriverInfo.last_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedDriverInfo.fleet?.brand} {selectedDriverInfo.fleet?.model} •{" "}
                    {selectedDriverInfo.fleet?.color}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                      🚗 {selectedDriverInfo.fleet?.plate_number}
                    </span>
                    <span
                      className={`inline-flex items-center text-xs px-2 py-0.5 rounded ${
                        selectedDriverInfo.is_online !== false
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {selectedDriverInfo.is_online !== false ? "● Online" : "● Offline"}
                    </span>
                    <span className="text-xs text-gray-400">
                      {selectedDriverInfo.fleet?.passenger_seat} seats •{" "}
                      {selectedDriverInfo.fleet?.has_ac ? "AC" : "No AC"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* FIX P2: Dispatch Progress Tracker */}
          {dispatchProgress.active && (
            <div className="bg-white shadow-lg rounded-lg p-4" id="dispatch-progress">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-blue-700">
                  🔍 Searching for Drivers...
                </h3>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                  {dispatchProgress.driversTriedCount} tried
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5 mb-2">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(dispatchProgress.driversTriedCount * 10, 100)}%`,
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
              </div>
              <div
                className="max-h-24 overflow-y-auto text-xs text-gray-500 space-y-0.5"
                style={{ scrollBehavior: "smooth" }}
              >
                {dispatchProgress.messages.map((msg, idx) => (
                  <p key={idx} className={idx === dispatchProgress.messages.length - 1 ? "text-gray-700 font-medium" : ""}>
                    {msg}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 bg-white shadow-lg flex flex-col items-center relative">
          {/* FIX P2: Driver Count & Search Bar */}
          <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
            <span className="bg-white shadow-md text-xs font-semibold px-3 py-1.5 rounded-full text-gray-700 border">
              🚕 {filteredDrivers.length} driver{filteredDrivers.length !== 1 ? "s" : ""} online
            </span>
            <input
              type="text"
              placeholder="Search driver or plate..."
              value={driverSearchQuery}
              onChange={(e) => handleDriverSearch(e.target.value)}
              className="bg-white shadow-md text-xs px-3 py-1.5 rounded-full border outline-none focus:ring-2 focus:ring-blue-300"
              style={{ width: "180px" }}
              id="driver-search-input"
            />
          </div>

          <APIProvider apiKey={GOOGLE_API_KEY}>
            <Map
              defaultZoom={14}
              defaultCenter={currentLocation}
              onClick={requestLoading.loading ? undefined : handleMapClick}
              disableDefaultUI={requestLoading.loading}
              gestureHandling={requestLoading.loading ? "none" : "auto"}
            >
              <DispatchMapMarkers
                driverLocation={filteredDrivers}
                pickupLocation={pickupLocation}
                dropOffLocation={dropOffLocation}
                requestLoading={requestLoading}
                handleSetDriver={handleSetDriver}
              />
            </Map>
          </APIProvider>
        </div>
      </div>
    </div>
  );
}
